/**
 * HX Operations Center — Presentations Mode.
 *
 * Central presentation hub with three groups:
 *   • Executive Decks (native React routes: /neugreen, /neunexus, /mionga)
 *   • Document Presentations (indexed PDFs from /api/ops/documents)
 *   • Uploaded PPTX (raw file download links)
 *
 * All content opens in a fullscreen iframe modal — user never leaves /ops.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Presentation, FileText, Maximize2, X, ExternalLink,
  Download, Layers, Loader2,
} from "lucide-react";
import axios from "axios";

const P = {
  bg:      "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E4E7EB",
  dim:     "rgba(228,231,235,0.62)",
  accent:  "#3B82F6",
  accent2: "#22C55E",
  silver:  "#C0C7CE",
  amber:   "#F59E0B",
};

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

// Native React decks live inside the same SPA — iframe them straight in.
const NATIVE_DECKS = [
  {
    id: "neugreen",
    title: "NeuGreen Energy",
    subtitle: "Intelligent Energy Ecosystem Germany",
    tag: "12 slides · DE · EN · PT",
    supplier: "NeuGreen",
    accent: "#22C55E",
    url: "/neugreen",
    thumbBg: "linear-gradient(135deg, #0F1114 0%, #143923 45%, #22C55E33 100%)",
    icon: "🌱",
  },
  {
    id: "neunexus",
    title: "NeuNexus Energy",
    subtitle: "Uetze Consortium · Industry 4.0",
    tag: "15 slides · PT · EN · DE",
    supplier: "NeuNexus",
    accent: "#14FF8C",
    url: "/neunexus",
    thumbBg: "linear-gradient(135deg, #04070E 0%, #0B1E1B 55%, #14FF8C33 100%)",
    icon: "⚙",
  },
  {
    id: "mionga",
    title: "MIONGA Beverages",
    subtitle: "Hospitality Ecosystem · Cape Verde → Germany",
    tag: "Institutional deck · PT · EN · DE · FR",
    supplier: "MIONGA",
    accent: "#D4A857",
    url: "/mionga",
    thumbBg: "linear-gradient(135deg, #0A0F1A 0%, #2B1F14 50%, #D4A85733 100%)",
    icon: "◆",
  },
];

const isPresentationDoc = (d) => {
  const ext = (d.mime_type || "").toLowerCase();
  const filename = (d.original_filename || "").toLowerCase();
  if (ext.includes("presentation") || filename.endsWith(".pptx")) return true;
  if ((d.category || "").toLowerCase() === "presentation") return true;
  return false;
};

const isViewablePdf = (d) => {
  const ext = (d.mime_type || "").toLowerCase();
  const filename = (d.original_filename || "").toLowerCase();
  return ext.includes("pdf") || filename.endsWith(".pdf");
};

const DeckCard = ({ deck, onOpen }) => (
  <button
    onClick={() => onOpen({ kind: "native", url: deck.url, title: deck.title, subtitle: deck.subtitle })}
    data-testid={`presentation-deck-${deck.id}`}
    className="group text-left rounded-lg overflow-hidden transition hover:border-white/25"
    style={{ background: P.surface, border: `1px solid ${P.border}` }}
  >
    <div
      className="relative aspect-[16/10] overflow-hidden flex items-center justify-center"
      style={{ background: deck.thumbBg }}
    >
      <div
        className="text-[64px] font-bold tracking-tight opacity-90 transition group-hover:scale-110"
        style={{ color: deck.accent, textShadow: `0 8px 40px ${deck.accent}55`, fontFamily: "'Inter', system-ui" }}
      >
        {deck.icon}
      </div>
      <div className="absolute inset-0 flex items-end p-4 bg-gradient-to-t from-black/60 via-transparent to-transparent">
        <div>
          <div className="text-[9px] uppercase tracking-[0.28em] font-semibold" style={{ color: deck.accent }}>
            Executive Deck
          </div>
          <div className="mt-1 text-[16px] font-bold" style={{ color: "#fff" }}>{deck.title}</div>
        </div>
      </div>
      <div className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition group-hover:scale-110"
        style={{ background: `${deck.accent}dd`, color: "#0A0F1A" }}
      >
        <Maximize2 size={13} />
      </div>
    </div>
    <div className="p-4">
      <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>{deck.subtitle}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{deck.tag}</div>
    </div>
  </button>
);

const DocCard = ({ doc, onOpen, kind }) => {
  const accent = kind === "pdf" ? P.accent : P.amber;
  return (
    <button
      onClick={() => onOpen({
        kind: kind === "pdf" ? "pdf" : "download",
        url: `${API}/ops/documents/${doc.id}/download`,
        title: doc.title || doc.original_filename,
        subtitle: `${doc.supplier_id || "Unassigned"} · ${doc.pages} page${doc.pages !== 1 ? "s" : ""} · ${doc.language?.toUpperCase() || "?"}`,
        docId: doc.id,
      })}
      data-testid={`presentation-doc-${doc.id}`}
      className="group text-left rounded-lg overflow-hidden transition hover:border-white/25"
      style={{ background: P.surface, border: `1px solid ${P.border}` }}
    >
      <div
        className="relative aspect-[16/10] overflow-hidden flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${P.bg} 0%, ${accent}22 100%)` }}
      >
        <FileText size={48} style={{ color: accent, opacity: 0.85 }} />
        <div className="absolute inset-0 flex items-end p-4 bg-gradient-to-t from-black/60 via-transparent to-transparent">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.28em] font-semibold" style={{ color: accent }}>
              {kind === "pdf" ? "PDF · Inline" : "PPTX · Download"}
            </div>
            <div className="mt-1 text-[13.5px] font-semibold truncate" style={{ color: "#fff" }}>
              {doc.title || doc.original_filename}
            </div>
          </div>
        </div>
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition group-hover:scale-110"
          style={{ background: `${accent}dd`, color: "#0A0F1A" }}
        >
          {kind === "pdf" ? <Maximize2 size={13} /> : <Download size={13} />}
        </div>
      </div>
      <div className="p-4">
        <div className="text-[11.5px]" style={{ color: P.dim }}>
          <span className="uppercase tracking-wider" style={{ color: P.silver }}>
            {doc.supplier_id || "Unassigned"}
          </span>
          <span className="mx-1.5 opacity-40">·</span>
          <span>{doc.pages} page{doc.pages !== 1 ? "s" : ""}</span>
          <span className="mx-1.5 opacity-40">·</span>
          <span>{doc.language?.toUpperCase() || "?"}</span>
        </div>
      </div>
    </button>
  );
};

const PresentationViewer = ({ target, onClose }) => {
  const iframeRef = React.useRef(null);

  // Trap Esc for closing — both on the parent window and inside the iframe
  // (same-origin content shares access to the contentWindow).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    let cleanup = () => window.removeEventListener("keydown", onKey);
    const attachToFrame = () => {
      try {
        const win = iframeRef.current?.contentWindow;
        if (win) {
          win.addEventListener("keydown", onKey);
          const prev = cleanup;
          cleanup = () => { prev(); try { win.removeEventListener("keydown", onKey); } catch { /* cross-origin */ } };
        }
      } catch { /* cross-origin — ignore */ }
    };
    // Attach after the iframe loads
    const iframe = iframeRef.current;
    if (iframe) iframe.addEventListener("load", attachToFrame);
    return () => {
      if (iframe) iframe.removeEventListener("load", attachToFrame);
      cleanup();
    };
  }, [onClose, target?.url]);

  const renderInner = () => {
    if (target.kind === "native" || target.kind === "pdf") {
      // PDFs served with Content-Disposition: inline render natively in Chromium/Firefox
      const src = target.kind === "pdf" ? `${target.url}#toolbar=1&navpanes=0` : target.url;
      return (
        <iframe
          ref={iframeRef}
          src={src}
          title={target.title}
          className="w-full h-full border-0"
          style={{ background: "#000" }}
          data-testid="presentation-viewer-iframe"
          allow="fullscreen; autoplay; clipboard-read; clipboard-write"
          allowFullScreen
        />
      );
    }
    // Download-only fallback for PPTX/DOCX
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-8" style={{ color: P.ink }}>
        <FileText size={48} style={{ color: P.amber }} />
        <div className="text-[16px] font-semibold text-center max-w-lg">{target.title}</div>
        <div className="text-[12px]" style={{ color: P.dim }}>{target.subtitle}</div>
        <a
          href={target.url}
          download
          data-testid="presentation-viewer-download"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[13px] font-semibold"
          style={{ background: P.accent, color: "#fff" }}
        >
          <Download size={14} /> Download presentation
        </a>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex flex-col"
      style={{ background: "rgba(4,7,14,0.98)", backdropFilter: "blur(24px)" }}
      data-testid="presentation-viewer-modal"
    >
      <div
        className="flex items-center justify-between px-5 py-3 border-b shrink-0"
        style={{ borderColor: P.border, background: "rgba(255,255,255,0.02)" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: `${P.accent}22`, color: P.accent }}>
            <Presentation size={14} />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate" style={{ color: "#fff" }}>{target.title}</div>
            {target.subtitle && (
              <div className="text-[10.5px] uppercase tracking-[0.22em] truncate" style={{ color: P.silver }}>{target.subtitle}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(target.kind === "native" || target.kind === "pdf") && (
            <a
              href={target.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="presentation-viewer-open-new-tab"
              title="Open in new tab"
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/[0.08]"
              style={{ color: P.silver }}
            >
              <ExternalLink size={14} />
            </a>
          )}
          <button
            onClick={onClose}
            data-testid="presentation-viewer-close"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/[0.08]"
            style={{ color: P.silver }}
            title="Close (Esc)"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {renderInner()}
      </div>
    </motion.div>
  );
};

const Group = ({ eyebrow, title, count, children }) => (
  <div className="mb-10">
    <div className="flex items-baseline justify-between mb-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.accent }}>{eyebrow}</div>
        <h2 className="mt-1.5 font-bold tracking-tight text-[22px] lg:text-[26px]" style={{ color: "#fff" }}>{title}</h2>
      </div>
      <div className="text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>{count}</div>
    </div>
    {children}
  </div>
);

export default function PresentationsSection() {
  const [documents, setDocuments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [target, setTarget] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/ops/documents`);
        if (mounted) setDocuments(r.data || []);
      } catch (e) {
        // best-effort — decks are still shown
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const pdfDocs = documents.filter(isViewablePdf);
  const pptxDocs = documents.filter(isPresentationDoc);

  return (
    <div data-testid="presentations-section">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.accent }}>
          Presentation Center
        </div>
        <h1 className="mt-2 font-bold tracking-tight text-[32px] lg:text-[40px] leading-tight" style={{ color: "#fff" }}>
          Presentations.
        </h1>
        <p className="mt-3 text-[14px] max-w-2xl" style={{ color: P.dim }}>
          Every executive deck, PDF and technical document — presented inline. Open any presentation fullscreen without leaving the Operations Center.
        </p>
      </div>

      <Group eyebrow="BSGOTECH" title="Executive decks." count={`${NATIVE_DECKS.length} native decks`}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {NATIVE_DECKS.map((d) => <DeckCard key={d.id} deck={d} onOpen={setTarget} />)}
        </div>
      </Group>

      <Group
        eyebrow="Document Library"
        title="PDF presentations."
        count={loading ? "Loading…" : `${pdfDocs.length} PDF${pdfDocs.length !== 1 ? "s" : ""} indexed`}
      >
        {loading ? (
          <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
            <Loader2 size={12} className="animate-spin" /> Loading library…
          </div>
        ) : pdfDocs.length === 0 ? (
          <div
            className="rounded-lg p-6 text-[12.5px]"
            style={{ background: P.surface, border: `1px solid ${P.border}`, color: P.dim }}
          >
            <Layers size={16} className="mb-2" style={{ color: P.silver }} />
            No PDF documents indexed yet. Upload one from the <span style={{ color: P.accent }}>BS Assistente</span> module to make it appear here.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pdfDocs.map((d) => <DocCard key={d.id} doc={d} onOpen={setTarget} kind="pdf" />)}
          </div>
        )}
      </Group>

      {pptxDocs.length > 0 && (
        <Group eyebrow="Uploaded Slides" title="PPTX decks." count={`${pptxDocs.length} deck${pptxDocs.length !== 1 ? "s" : ""}`}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pptxDocs.map((d) => <DocCard key={d.id} doc={d} onOpen={setTarget} kind="pptx" />)}
          </div>
        </Group>
      )}

      <AnimatePresence>
        {target && <PresentationViewer target={target} onClose={() => setTarget(null)} />}
      </AnimatePresence>
    </div>
  );
}
